import React from 'react';
import { AlertTriangle, Archive, ChevronDown, ChevronRight, FileText, Inbox, MailPlus, Send, ShieldAlert, Star, Tag, Trash2, User } from 'lucide-react';
import { AppSelect } from '../../../components/AppSelect';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { folderLabelsForRail, isCategoryFolder } from '../folderNames';
import {
  getClassificationLabel,
  inboxEmailDragHasIds,
  MAIL_STATUS_DOT,
  MAILBOX_STATUSES,
  parseInboxEmailDragIds,
  type MailStatus,
} from '../inboxShared';

export type InboxFolderRow = {
  id: string;
  external_folder_id: string;
  name: string;
  display_name: string;
  attributes: string[];
  system_folder: boolean;
  unread_count: number;
  total_count: number;
  description: string | null;
};

/** Posteingang und Gesendet folgen der Absender-Richtung, nicht der Anbieter-Ordner-ID. */
export function mailboxDirectionForFolder(
  folder: Pick<InboxFolderRow, 'attributes' | 'external_folder_id' | 'name'> | undefined,
): 'in' | 'out' | null {
  if (!folder) return null;
  const attrs = folder.attributes ?? [];
  const id = String(folder.external_folder_id ?? '').toUpperCase();
  const name = String(folder.name ?? '').toUpperCase();
  if (attrs.includes('\\Sent') || id === 'SENT' || name === 'SENT') return 'out';
  if (attrs.includes('\\Inbox') || id === 'INBOX' || name === 'INBOX') return 'in';
  return null;
}

function folderCount(folder: InboxFolderRow, mailboxCounts?: { in: number; out: number }): number {
  const direction = mailboxDirectionForFolder(folder);
  if (direction === 'in') return mailboxCounts?.in ?? 0;
  if (direction === 'out') return mailboxCounts?.out ?? 0;
  return folder.total_count ?? 0;
}

const PIN_KEYS = ['inbox', 'starred', 'sent', 'archive', 'trash'] as const;
type PinKey = (typeof PIN_KEYS)[number];

function pinKey(folder: InboxFolderRow): PinKey | null {
  const attrs = folder.attributes ?? [];
  const id = String(folder.external_folder_id ?? '').toUpperCase();
  const name = String(folder.name ?? '').toUpperCase();
  if (attrs.includes('\\Inbox') || id === 'INBOX') return 'inbox';
  if (id === 'STARRED' || name === 'STARRED' || attrs.includes('\\Flagged')) return 'starred';
  if (attrs.includes('\\Sent') || id === 'SENT' || name === 'SENT') return 'sent';
  if (attrs.includes('\\Archive') || id === 'ARCHIVE' || name === 'ARCHIVE') return 'archive';
  if (attrs.includes('\\Trash') || id === 'TRASH' || name === 'TRASH') return 'trash';
  return null;
}

const SYSTEM_ORDER = ['\\Inbox', '\\Sent', '\\Drafts', '\\Junk', '\\Trash', '\\Archive', '\\Important'];

function systemRank(folder: InboxFolderRow): number {
  const hit = (folder.attributes ?? []).find((attr) => SYSTEM_ORDER.includes(attr));
  return hit ? SYSTEM_ORDER.indexOf(hit) : 50;
}

const ICON_SLOT = 'inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center';
const ROW_CLASS =
  'flex w-full items-center gap-3.5 rounded-full px-3 h-7 text-sm text-left transition-colors';
const ROW_IDLE = 'font-normal text-app-text-muted hover:bg-app-hover';
const ROW_ACTIVE = 'bg-inbox-selected-strong font-semibold text-inbox-selected-text';
const ROW_UNREAD = 'font-bold text-app-text hover:bg-app-hover';
const ROW_DROP = 'bg-inbox-drop font-semibold text-inbox-selected-text ring-2 ring-inset ring-pipedrive-blue/40';
/** Gmail mobile drawer: tall touch rows whose highlight runs flush into the left edge. */
const DRAWER_ROW_CLASS =
  'flex w-full items-center gap-5 rounded-r-full pl-6 pr-5 h-12 text-[15px] text-left transition-colors active:bg-app-hover';

/** True inside the mobile drawer, so rows render touch-sized. */
const DrawerContext = React.createContext(false);

/** Open/closed sections, plus the "Nur neue" filter on categories. */
type NavSectionKey = 'categories' | 'flags' | 'status' | 'assignees' | 'categoriesUnreadOnly';

function navSectionsStorageKey(): string {
  let instanceId = 'default';
  try {
    instanceId = localStorage.getItem('activeInstanceId') || instanceId;
  } catch {
    /* privater Modus */
  }
  return `inbox-nav-sections:${instanceId}`;
}

function readNavSectionOpen(key: NavSectionKey, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(navSectionsStorageKey());
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Record<NavSectionKey, unknown>>;
    return typeof parsed[key] === 'boolean' ? parsed[key] : fallback;
  } catch {
    return fallback;
  }
}

function writeNavSectionOpen(key: NavSectionKey, open: boolean) {
  try {
    const raw = localStorage.getItem(navSectionsStorageKey());
    const parsed = raw ? (JSON.parse(raw) as Partial<Record<NavSectionKey, boolean>>) : {};
    parsed[key] = open;
    localStorage.setItem(navSectionsStorageKey(), JSON.stringify(parsed));
  } catch {
    /* Speicher voll oder blockiert */
  }
}

/** Gmail-style label outline, drawn to match lucide stroke icons. */
function LabelIcon({ className, strokeWidth = 2 }: { className?: string; strokeWidth?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M3 8a2 2 0 0 1 2-2h11.1a2 2 0 0 1 1.6.8L21.5 12l-3.8 5.2a2 2 0 0 1-1.6.8H5a2 2 0 0 1-2-2Z" />
    </svg>
  );
}

function folderIcon(folder: InboxFolderRow) {
  const attrs = folder.attributes ?? [];
  const id = folder.external_folder_id;
  if (attrs.includes('\\Inbox') || id === 'INBOX') return Inbox;
  if (id === 'STARRED' || folder.name === 'STARRED') return Star;
  if (attrs.includes('\\Sent')) return Send;
  if (attrs.includes('\\Drafts') || id === 'DRAFT' || id === 'DRAFTS' || folder.name === 'DRAFTS') return FileText;
  if (attrs.includes('\\Archive')) return Archive;
  if (attrs.includes('\\Trash')) return Trash2;
  if (attrs.includes('\\Junk')) return ShieldAlert;
  return Tag;
}

export type InboxAssigneeCount = { user_id: string; count: number };

export type InboxMemberOption = { id: string; label: string };

export type InboxStatusCounts = {
  offen: number;
  done: number;
  waiting_for_reply: number;
  spam: number;
  other: number;
  urgent: number;
};

const EMPTY_STATUS_COUNTS: InboxStatusCounts = {
  offen: 0,
  done: 0,
  waiting_for_reply: 0,
  spam: 0,
  other: 0,
  urgent: 0,
};

function NavRow({
  active,
  label,
  count,
  onClick,
  icon: Icon,
  dotClass,
  title,
  unread = false,
  dropActive = false,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  dotClass?: string;
  title?: string;
  unread?: boolean;
  dropActive?: boolean;
  onDragOver?: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDragLeave?: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLButtonElement>) => void;
}) {
  const drawer = React.useContext(DrawerContext);
  const tone = dropActive ? ROW_DROP : active ? ROW_ACTIVE : unread ? ROW_UNREAD : ROW_IDLE;
  const iconSize = drawer ? 'h-5 w-5' : 'h-[18px] w-[18px]';
  return (
    <button
      type="button"
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      aria-pressed={active}
      title={title || label}
      className={`${drawer ? DRAWER_ROW_CLASS : ROW_CLASS} ${tone}`}
    >
      <span className={`inline-flex ${iconSize} shrink-0 items-center justify-center`}>
        {dotClass ? <span className={`h-2.5 w-2.5 ${drawer ? 'rounded-full' : 'rounded-sm'} ${dotClass}`} /> : null}
        {Icon ? <Icon className={iconSize} strokeWidth={1.6} /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count != null && count > 0 ? (
        <span className={`shrink-0 tabular-nums ${unread || active ? 'font-semibold' : 'text-[13px]'}`}>
          {count.toLocaleString('de-DE')}
        </span>
      ) : null}
    </button>
  );
}

function SectionHeader({
  open,
  label,
  onToggle,
  variant = 'label',
  action,
}: {
  open: boolean;
  label: string;
  onToggle: () => void;
  variant?: 'label' | 'row';
  /** Rendered beside the toggle, not inside it, so it stays its own button. */
  action?: React.ReactNode;
}) {
  const drawer = React.useContext(DrawerContext);
  const isLabel = variant === 'label';
  if (action) {
    return (
      <div className={`flex items-center gap-1 ${drawer ? 'pr-4' : 'pr-1'}`}>
        <div className="min-w-0 flex-1">
          <SectionHeader open={open} label={label} onToggle={onToggle} variant={variant} />
        </div>
        {action}
      </div>
    );
  }
  if (drawer && isLabel) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex h-11 w-full items-center gap-2 pl-6 pr-5 text-left text-xs font-medium uppercase tracking-wide text-app-text-subtle"
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={
        drawer
          ? `${DRAWER_ROW_CLASS} ${ROW_IDLE}`
          : isLabel
          ? 'flex h-8 w-full items-center gap-2 rounded-full px-3 text-left text-sm font-medium text-app-text hover:bg-app-hover'
          : `${ROW_CLASS} w-full ${ROW_IDLE}`
      }
    >
      <span className={isLabel ? 'inline-flex h-4 w-4 shrink-0 items-center justify-center' : ICON_SLOT}>
        {open ? (
          <ChevronDown className={isLabel ? 'h-4 w-4' : 'h-[18px] w-[18px]'} strokeWidth={isLabel ? 2 : 1.6} />
        ) : (
          <ChevronRight className={isLabel ? 'h-4 w-4' : 'h-[18px] w-[18px]'} strokeWidth={isLabel ? 2 : 1.6} />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
}

export function InboxFolderNav({
  accountId,
  selectedFolderId,
  starredOnly,
  urgentOnly,
  activeStatus,
  activeAssigneeId = null,
  members = [],
  onSelectFolder,
  onSelectStarred,
  onSelectUrgent,
  onSelectStatus,
  onSelectAssignee,
  onNewMail,
  onAssignEmailsToCategory,
  onAssignEmailsUrgent,
  onAssignEmailsToStatus,
  onAssignEmailsToUser,
  onFoldersChange,
  accountOptions,
  selectedAccountId,
  onAccountChange,
  mailboxCounts,
  countsRevision = 0,
  mobileOpen,
  onCloseMobile,
}: {
  accountId: string | number | null;
  selectedFolderId: string | null;
  starredOnly: boolean;
  urgentOnly: boolean;
  activeStatus: string | null;
  activeAssigneeId?: string | null;
  members?: InboxMemberOption[];
  onSelectFolder: (externalId: string) => void;
  onSelectStarred: () => void;
  onSelectUrgent: () => void;
  onSelectStatus: (status: MailStatus | null) => void;
  onSelectAssignee: (userId: string | null) => void;
  onNewMail: () => void;
  onAssignEmailsToCategory?: (emailIds: string[], folderExternalId: string) => void;
  onAssignEmailsUrgent?: (emailIds: string[]) => void;
  onAssignEmailsToStatus?: (emailIds: string[], status: MailStatus) => void;
  onAssignEmailsToUser?: (emailIds: string[], userId: string) => void;
  onFoldersChange?: (folders: InboxFolderRow[]) => void;
  accountOptions: Array<{ value: string; label: string }>;
  selectedAccountId: string | null;
  onAccountChange: (value: string | null) => void;
  mailboxCounts?: { in: number; out: number };
  countsRevision?: number;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}) {
  const [folders, setFolders] = React.useState<InboxFolderRow[]>([]);
  const [statusCounts, setStatusCounts] = React.useState<InboxStatusCounts>(EMPTY_STATUS_COUNTS);
  const [assigneeCounts, setAssigneeCounts] = React.useState<InboxAssigneeCount[]>([]);
  const [assigneesOpen, setAssigneesOpen] = React.useState(() => readNavSectionOpen('assignees', true));
  const [loading, setLoading] = React.useState(false);
  const [dropTargetId, setDropTargetId] = React.useState<string | null>(null);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [categoriesOpen, setCategoriesOpen] = React.useState(() => readNavSectionOpen('categories', true));
  const [categoriesUnreadOnly, setCategoriesUnreadOnly] = React.useState(() => readNavSectionOpen('categoriesUnreadOnly', false));
  const [flagsOpen, setFlagsOpen] = React.useState(() => readNavSectionOpen('flags', true));
  const [statusOpen, setStatusOpen] = React.useState(() => readNavSectionOpen('status', true));
  const onFoldersChangeRef = React.useRef(onFoldersChange);
  onFoldersChangeRef.current = onFoldersChange;

  const load = React.useCallback(async (id: string | number) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/inbox/accounts/${id}/folders`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const rows = (Array.isArray(data.folders) ? data.folders : []) as InboxFolderRow[];
      const counts = data.status_counts as Partial<InboxStatusCounts> | undefined;
      const assignees = Array.isArray(data.assignee_counts) ? data.assignee_counts : [];
      setFolders(rows);
      setAssigneeCounts(
        assignees
          .map((row: { user_id?: unknown; count?: unknown }) => ({
            user_id: String(row.user_id ?? ''),
            count: Number(row.count ?? 0),
          }))
          .filter((row: InboxAssigneeCount) => row.user_id && row.count > 0),
      );
      setStatusCounts({
        offen: Number(counts?.offen ?? 0),
        done: Number(counts?.done ?? 0),
        waiting_for_reply: Number(counts?.waiting_for_reply ?? 0),
        spam: Number(counts?.spam ?? 0),
        other: Number(counts?.other ?? 0),
        urgent: Number(counts?.urgent ?? 0),
      });
      onFoldersChangeRef.current?.(rows);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    setFolders([]);
    setStatusCounts(EMPTY_STATUS_COUNTS);
    setAssigneeCounts([]);
    if (accountId == null) return;
    void load(accountId);
  }, [accountId, load]);

  React.useEffect(() => {
    if (accountId == null || countsRevision === 0) return;
    void load(accountId);
  }, [accountId, countsRevision, load]);

  const systemFolders = React.useMemo(
    () => folders.filter((folder) => !isCategoryFolder(folder)).slice().sort((a, b) => systemRank(a) - systemRank(b) || a.display_name.localeCompare(b.display_name, 'de')),
    [folders],
  );
  const customFolders = React.useMemo(
    () => folders.filter(isCategoryFolder).slice().sort((a, b) => a.name.localeCompare(b.name, 'de')),
    [folders],
  );
  // The open category stays listed, so it does not vanish once its mail is read.
  const visibleCategoryFolders = React.useMemo(
    () =>
      categoriesUnreadOnly
        ? customFolders.filter((folder) => (folder.unread_count ?? 0) > 0 || folder.external_folder_id === selectedFolderId)
        : customFolders,
    [categoriesUnreadOnly, customFolders, selectedFolderId],
  );
  const customLabels = React.useMemo(
    () => folderLabelsForRail(customFolders.map((folder) => ({ id: folder.id, name: folder.name || folder.display_name }))),
    [customFolders],
  );
  const pinnedByKey = React.useMemo(() => {
    const map = new Map<PinKey, InboxFolderRow>();
    for (const folder of systemFolders) {
      const key = pinKey(folder);
      if (key && !map.has(key)) map.set(key, folder);
    }
    return map;
  }, [systemFolders]);
  const moreFolders = React.useMemo(
    () => systemFolders.filter((folder) => pinKey(folder) == null),
    [systemFolders],
  );
  const inboxFolder = pinnedByKey.get('inbox');
  const assigneeRows = React.useMemo(() => {
    const counts = new Map(assigneeCounts.map((row) => [row.user_id, row.count]));
    return members
      .map((member) => ({
        id: member.id,
        label: member.label,
        count: counts.get(member.id) ?? 0,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }, [assigneeCounts, members]);

  React.useEffect(() => {
    if (!inboxFolder || starredOnly || urgentOnly || activeStatus || activeAssigneeId) return;
    const selectionMatches = folders.some((folder) => folder.external_folder_id === selectedFolderId);
    if (selectionMatches) return;
    onSelectFolder(inboxFolder.external_folder_id);
  }, [folders, inboxFolder, selectedFolderId, starredOnly, urgentOnly, activeStatus, activeAssigneeId, onSelectFolder]);

  React.useEffect(() => {
    if (moreFolders.some((folder) => folder.external_folder_id === selectedFolderId)) setMoreOpen(true);
  }, [moreFolders, selectedFolderId]);

  const toggleNavSection = (key: NavSectionKey, setOpen: React.Dispatch<React.SetStateAction<boolean>>) => {
    setOpen((open) => {
      const next = !open;
      writeNavSectionOpen(key, next);
      return next;
    });
  };

  const acceptEmailDrop = (event: React.DragEvent<HTMLButtonElement>, targetId: string, enabled: boolean) => {
    if (!enabled || !inboxEmailDragHasIds(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropTargetId(targetId);
  };

  const clearDropTarget = (event: React.DragEvent<HTMLButtonElement>, targetId: string) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setDropTargetId((current) => (current === targetId ? null : current));
  };

  const finishEmailDrop = (event: React.DragEvent<HTMLButtonElement>, apply: (ids: string[]) => void) => {
    event.preventDefault();
    setDropTargetId(null);
    const ids = parseInboxEmailDragIds(event.dataTransfer);
    if (ids.length === 0) return;
    apply(ids);
  };

  const accountPicker =
    accountOptions.length > 1 ? (
      <AppSelect
        value={selectedAccountId ?? accountOptions[0]?.value ?? ''}
        onChange={(value) => onAccountChange(value || null)}
        options={accountOptions}
        variant="ghost"
        size="sm"
        aria-label="Postfach"
        className="mt-2"
        triggerClassName="!min-h-0 !h-auto !w-auto !rounded-md !px-1 !py-0 !text-xs !font-normal !text-app-text-subtle"
      />
    ) : (
      <p className="mt-2 truncate px-1 text-xs text-app-text-subtle">{accountOptions[0]?.label}</p>
    );

  const renderBody = (drawer: boolean) => (
    <DrawerContext.Provider value={drawer}>
    <div className="flex h-full min-h-0 flex-col">
      {drawer ? (
        // New mail lives on the floating button, so the drawer opens straight onto the folders.
        <div className="shrink-0 border-b border-app-border px-6 pb-3 pt-[max(1.25rem,env(safe-area-inset-top))]">
          <p className="text-lg font-semibold text-app-text">Postfach</p>
          <div className="-mx-1">{accountPicker}</div>
        </div>
      ) : (
      <div className="p-3">
        <button
          type="button"
          onClick={onNewMail}
          className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-pipedrive-blue px-3.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <MailPlus className="h-4 w-4" />
          Neue E-Mail
        </button>
        {accountPicker}
      </div>
      )}
      <div
        className={
          drawer
            ? 'min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain pb-[max(1rem,env(safe-area-inset-bottom))] pr-3 pt-2 [&>div+div]:border-t [&>div+div]:border-app-border [&>div+div]:pt-1'
            : 'min-h-0 flex-1 space-y-4 overflow-y-auto px-2 pb-4 app-scrollbar'
        }
      >
        {loading && folders.length === 0 ? (
          <InlineLoadingState label="Ordner werden geladen …" />
        ) : (
          <div className="space-y-0">
            {PIN_KEYS.map((key) => {
              if (key === 'starred') {
                const starredFolder = pinnedByKey.get('starred');
                return (
                  <NavRow
                    key="starred"
                    active={starredOnly}
                    label="Markiert"
                    count={starredFolder?.total_count}
                    icon={Star}
                    onClick={onSelectStarred}
                  />
                );
              }
              const folder = pinnedByKey.get(key);
              if (!folder) return null;
              const inboxUnread = key === 'inbox' && (folder.unread_count ?? 0) > 0;
              return (
                <NavRow
                  key={folder.id}
                  active={!starredOnly && !urgentOnly && !activeStatus && !activeAssigneeId && selectedFolderId === folder.external_folder_id}
                  label={folder.display_name || folder.name}
                  count={folderCount(folder, mailboxCounts)}
                  unread={inboxUnread}
                  icon={folderIcon(folder)}
                  onClick={() => onSelectFolder(folder.external_folder_id)}
                />
              );
            })}
            {moreOpen
              ? moreFolders.map((folder) => (
                  <NavRow
                    key={folder.id}
                    active={!starredOnly && !urgentOnly && !activeStatus && !activeAssigneeId && selectedFolderId === folder.external_folder_id}
                    label={folder.display_name || folder.name}
                    count={folder.total_count}
                    icon={folderIcon(folder)}
                    onClick={() => onSelectFolder(folder.external_folder_id)}
                  />
                ))
              : null}
            {moreFolders.length > 0 ? (
              <SectionHeader
                open={moreOpen}
                label="Mehr"
                variant="row"
                onToggle={() => setMoreOpen((open) => !open)}
              />
            ) : null}
          </div>
        )}
        <div>
          <SectionHeader
            open={categoriesOpen}
            label="Ordner"
            onToggle={() => toggleNavSection('categories', setCategoriesOpen)}
            action={
              categoriesOpen && customFolders.length > 0 ? (
                <button
                  type="button"
                  onClick={() => toggleNavSection('categoriesUnreadOnly', setCategoriesUnreadOnly)}
                  aria-pressed={categoriesUnreadOnly}
                  title={categoriesUnreadOnly ? 'Alle Ordner zeigen' : 'Nur Ordner mit ungelesenen Mails zeigen'}
                  className={`shrink-0 rounded-full px-2 h-6 text-xs transition-colors ${
                    categoriesUnreadOnly
                      ? 'bg-inbox-selected-strong font-semibold text-inbox-selected-text'
                      : 'text-app-text-muted hover:bg-app-hover'
                  }`}
                >
                  Nur neue
                </button>
              ) : null
            }
          />
          {categoriesOpen ? (
            <div className="space-y-0">
              {categoriesUnreadOnly && visibleCategoryFolders.length === 0 ? (
                <p className="px-3 py-1 text-xs text-app-text-muted">Keine ungelesenen Mails in Ordnern.</p>
              ) : null}
              {visibleCategoryFolders.map((folder) => {
                const shown = customLabels.get(folder.id);
                return (
                  <NavRow
                    key={folder.id}
                    active={!starredOnly && !urgentOnly && !activeStatus && !activeAssigneeId && selectedFolderId === folder.external_folder_id}
                    label={shown?.label || folder.display_name || folder.name}
                    title={shown?.title}
                    // Like Gmail labels: only unread mail is counted, nothing when all is read.
                    count={folder.unread_count}
                    unread={(folder.unread_count ?? 0) > 0}
                    icon={LabelIcon}
                    dropActive={dropTargetId === `category:${folder.id}`}
                    onClick={() => onSelectFolder(folder.external_folder_id)}
                    onDragOver={(event) => acceptEmailDrop(event, `category:${folder.id}`, Boolean(onAssignEmailsToCategory))}
                    onDragLeave={(event) => clearDropTarget(event, `category:${folder.id}`)}
                    onDrop={(event) => finishEmailDrop(event, (ids) => onAssignEmailsToCategory?.(ids, folder.external_folder_id))}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
        <div>
          <SectionHeader
            open={flagsOpen}
            label="Markierungen"
            onToggle={() => toggleNavSection('flags', setFlagsOpen)}
          />
          {flagsOpen ? (
            <div className="space-y-0">
              <NavRow
                active={urgentOnly}
                label="Dringend"
                count={statusCounts.urgent}
                icon={AlertTriangle}
                onClick={onSelectUrgent}
                dropActive={dropTargetId === 'flag:urgent'}
                onDragOver={(event) => acceptEmailDrop(event, 'flag:urgent', Boolean(onAssignEmailsUrgent))}
                onDragLeave={(event) => clearDropTarget(event, 'flag:urgent')}
                onDrop={(event) => finishEmailDrop(event, (ids) => onAssignEmailsUrgent?.(ids))}
              />
              <NavRow
                active={activeStatus === 'spam'}
                label="Spam"
                count={statusCounts.spam}
                icon={ShieldAlert}
                onClick={() => onSelectStatus(activeStatus === 'spam' ? null : 'spam')}
                dropActive={dropTargetId === 'flag:spam'}
                onDragOver={(event) => acceptEmailDrop(event, 'flag:spam', Boolean(onAssignEmailsToStatus))}
                onDragLeave={(event) => clearDropTarget(event, 'flag:spam')}
                onDrop={(event) => finishEmailDrop(event, (ids) => onAssignEmailsToStatus?.(ids, 'spam'))}
              />
            </div>
          ) : null}
        </div>
        <div>
          <SectionHeader
            open={statusOpen}
            label="Status"
            onToggle={() => toggleNavSection('status', setStatusOpen)}
          />
          {statusOpen ? (
            <div className="space-y-0">
              {MAILBOX_STATUSES.map((status) => (
                <NavRow
                  key={status}
                  active={activeStatus === status}
                  label={getClassificationLabel(status)}
                  count={statusCounts[status]}
                  dotClass={MAIL_STATUS_DOT[status]}
                  onClick={() => onSelectStatus(activeStatus === status ? null : status)}
                  dropActive={dropTargetId === `status:${status}`}
                  onDragOver={(event) => acceptEmailDrop(event, `status:${status}`, Boolean(onAssignEmailsToStatus))}
                  onDragLeave={(event) => clearDropTarget(event, `status:${status}`)}
                  onDrop={(event) => finishEmailDrop(event, (ids) => onAssignEmailsToStatus?.(ids, status))}
                />
              ))}
            </div>
          ) : null}
        </div>
        {members.length > 0 ? (
          <div>
            <SectionHeader
              open={assigneesOpen}
              label="Zugewiesen"
              onToggle={() => toggleNavSection('assignees', setAssigneesOpen)}
            />
            {assigneesOpen ? (
              <div className="space-y-0">
                {assigneeRows.map((row) => (
                  <NavRow
                    key={row.id}
                    active={activeAssigneeId === row.id}
                    label={row.label}
                    count={row.count}
                    icon={User}
                    onClick={() => onSelectAssignee(activeAssigneeId === row.id ? null : row.id)}
                    dropActive={dropTargetId === `assignee:${row.id}`}
                    onDragOver={(event) => acceptEmailDrop(event, `assignee:${row.id}`, Boolean(onAssignEmailsToUser))}
                    onDragLeave={(event) => clearDropTarget(event, `assignee:${row.id}`)}
                    onDrop={(event) => finishEmailDrop(event, (ids) => onAssignEmailsToUser?.(ids, row.id))}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
    </DrawerContext.Provider>
  );

  return (
    <>
      <nav className="hidden md:flex w-[240px] shrink-0 flex-col bg-app-bg min-h-0">{renderBody(false)}</nav>
      {mobileOpen ? (
        <>
          <div className="fixed inset-0 z-[36] bg-black/50 md:hidden" onClick={onCloseMobile} />
          <nav
            aria-label="Ordner"
            className="fixed inset-y-0 left-0 z-[37] w-[min(85vw,320px)] rounded-r-2xl bg-app-surface shadow-2xl md:hidden"
          >
            {renderBody(true)}
          </nav>
        </>
      ) : null}
    </>
  );
}
